import React, { useEffect, useMemo, useState } from "react";

import { formatKST } from "../utils/dateUtils";
import {
  deviceTypeBadgeClass,
  deviceTypeText,
  normalizeCameraDevices,
  normalizeDeviceTypeValue,
  normalizeWatchDevices,
  getGpsText,
} from "../utils/deviceUtils";
import { statusLabelText } from "../utils/statusUtils";
import useCameraStatus from "../hooks/useCameraStatus";
import { refreshCameraStatuses } from "../utils/cameraStatusStore";

import SummaryCard from "../components/common/SummaryCard";
import QuickFilterButton from "../components/common/QuickFilterButton";
import DeviceTypeSummaryCard from "../components/devices/DeviceTypeSummaryCard";
import DeviceTable from "../components/devices/DeviceTable";
import DeviceMobileList from "../components/devices/DeviceMobileList";

const DEVICES_POLL_MS = 30000;
const DEVICES_HIDDEN_POLL_MS = 60000;

// 온라인 상태 표시는 가벼운 /api/my-cameras/status 만 사용하므로
// 전체 목록(느린 /api/my-cameras)과 분리해 짧은 주기로 갱신한다.
const DEVICES_STATUS_POLL_MS = 5000;
const DEVICES_STATUS_HIDDEN_POLL_MS = 30000;
const DEVICES_CACHE_KEY = "monity.devices.cachedCameras.v1";

function readCachedCameras() {
  try {
    const raw = sessionStorage.getItem(DEVICES_CACHE_KEY);
    if (!raw) return [];

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    return parsed;
  } catch {
    return [];
  }
}

function writeCachedCameras(cameras) {
  try {
    if (!Array.isArray(cameras)) return;
    sessionStorage.setItem(DEVICES_CACHE_KEY, JSON.stringify(cameras));
  } catch {}
}

export default function DevicesPage({ me, permissionFlags }) {
  const canWatch = !!permissionFlags?.canWatchDanger;

  const [cameras, setCameras] = useState(() => readCachedCameras());
  const [watches, setWatches] = useState([]);

  // 카메라 상태는 공유 스토어에서 받는다(이 화면이 직접 폴링하지 않는다).
  const { statuses: cameraStatuses } = useCameraStatus();

  const [loading, setLoading] = useState(() => readCachedCameras().length === 0);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [quickFilter, setQuickFilter] = useState("ALL");
  const [devicePage, setDevicePage] = useState(1);

  // 즉시 한 번 갱신이 필요할 때만 쓴다(새로고침 버튼·목록 재조회 직전 등).
  // 주기 폴링은 공유 스토어가 맡으므로 여기서 타이머를 돌리지 않는다.
  const fetchCameraStatuses = async () => {
    const statuses = await refreshCameraStatuses();
    setLastUpdated(new Date());
    return Array.isArray(statuses) ? statuses : [];
  };

  const fetchWatches = async () => {
    if (!canWatch) {
      setWatches([]);
      return [];
    }

    try {
      const res = await fetch("/api/all-watch-data", {
        credentials: "include",
        cache: "no-store",
      });

      const data = await res.json().catch(() => []);

      const nextWatches = Array.isArray(data)
        ? data
        : Array.isArray(data?.items)
        ? data.items
        : [];

      setWatches(nextWatches);
      return nextWatches;
    } catch (err) {
      console.error("DevicesPage fetchWatches error:", err);
      setWatches([]);
      return [];
    }
  };

  const fetchCameras = async () => {
    try {
      const res = await fetch("/api/my-cameras", {
        credentials: "include",
        cache: "no-store",
      });

      const data = await res.json().catch(() => ({}));

      if (res.ok && data?.success && Array.isArray(data?.cameras)) {
        setCameras(data.cameras);
        writeCachedCameras(data.cameras);
        setLastUpdated(new Date());
        return data.cameras;
      }

      return [];
    } catch (err) {
      console.error("DevicesPage fetchCameras error:", err);
      return [];
    }
  };

  const fetchDevices = async ({ silent = false } = {}) => {
    const hasCachedCameras = Array.isArray(cameras) && cameras.length > 0;

    try {
      if (silent) {
        setRefreshing(true);
      } else if (!hasCachedCameras) {
        setLoading(true);
      }

      /*
       * 상태 API(/api/my-cameras/status)와 워치 API를 먼저 백그라운드로 갱신하고,
       * 카메라 목록 API는 도착하는 즉시 따로 반영해 화면이 빨리 뜨게 한다.
       * (과거 인증 이벤트루프 블로킹으로 목록 API가 느렸던 문제는 해결됨)
       */
      fetchCameraStatuses();
      fetchWatches();

      const nextCameras = await fetchCameras();

      if (Array.isArray(nextCameras) && nextCameras.length === 0 && !hasCachedCameras) {
        setCameras([]);
      }
    } catch (err) {
      console.error("DevicesPage fetchDevices error:", err);

      if (!hasCachedCameras) {
        setCameras([]);
      }

      // 카메라 상태는 공유 스토어가 들고 있으므로 여기서 비우지 않는다
      // (목록 조회가 실패했다고 이미 받아둔 온라인 상태까지 버릴 이유가 없다).
      setWatches([]);
      setLastUpdated(new Date());
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    let timer = null;

    const getNextDelay = () => {
      if (typeof document !== "undefined" && document.hidden) {
        return DEVICES_HIDDEN_POLL_MS;
      }

      return DEVICES_POLL_MS;
    };

    const run = async ({ silent = false } = {}) => {
      if (cancelled) return;

      await fetchDevices({ silent });

      if (cancelled) return;

      timer = setTimeout(() => {
        run({ silent: true });
      }, getNextDelay());
    };

    const handleVisibilityChange = () => {
      if (cancelled) return;

      if (timer) clearTimeout(timer);

      timer = setTimeout(() => {
        run({ silent: true });
      }, 500);
    };

    run({ silent: false });

    // 온라인 상태만 짧은 주기로 별도 갱신 (전체 목록 폴링과 분리)
    let statusTimer = null;

    // 카메라 상태는 공유 스토어가 폴링한다. 여기서는 워치만 따로 갱신한다.
    // (워치는 다른 API라 스토어에 포함되지 않는다)
    const statusLoop = async () => {
      if (cancelled) return;

      await fetchWatches();

      if (cancelled) return;

      const delay =
        typeof document !== "undefined" && document.hidden
          ? DEVICES_STATUS_HIDDEN_POLL_MS
          : DEVICES_STATUS_POLL_MS;

      statusTimer = setTimeout(statusLoop, delay);
    };

    statusTimer = setTimeout(statusLoop, DEVICES_STATUS_POLL_MS);

    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibilityChange);
    }

    return () => {
      cancelled = true;

      if (timer) clearTimeout(timer);
      if (statusTimer) clearTimeout(statusTimer);

      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      }
    };
  }, [canWatch]);

  const allDevices = useMemo(() => {
    const cameraRows = normalizeCameraDevices(cameras, cameraStatuses);
    const watchRows = canWatch ? normalizeWatchDevices(watches) : [];

    return [...cameraRows, ...watchRows].sort((a, b) => {
      const typeOrder = (item) => {
        const v = normalizeDeviceTypeValue(item?.deviceType);

        if (v === "MOBILE_CCTV") return 0;
        if (v === "MINTCAM") return 1;

        return 2;
      };

      const aType = typeOrder(a);
      const bType = typeOrder(b);

      if (aType !== bType) return aType - bType;

      // 같은 종류면 카메라 번호(camNo) 오름차순 우선
      const aNo = Number(a?.camNo);
      const bNo = Number(b?.camNo);
      const aHas = Number.isFinite(aNo);
      const bHas = Number.isFinite(bNo);
      if (aHas && bHas && aNo !== bNo) return aNo - bNo;
      if (aHas !== bHas) return aHas ? -1 : 1;

      // 번호가 없거나 같으면 이름을 자연스러운 숫자 순서로 비교
      return String(a.name || "").localeCompare(String(b.name || ""), "ko", {
        numeric: true,
      });
    });
  }, [cameras, cameraStatuses, watches, canWatch]);

  const summary = useMemo(() => {
    const total = allDevices.length;

    const online = allDevices.filter(
      (item) => String(item?.status || "").toUpperCase() === "ONLINE"
    ).length;

    const offline = allDevices.filter(
      (item) => String(item?.status || "").toUpperCase() === "OFFLINE"
    ).length;

    const disabled = allDevices.filter(
      (item) => String(item?.status || "").toUpperCase() === "DISABLED"
    ).length;

    const alert = allDevices.filter(
      (item) => String(item?.status || "").toUpperCase() === "ALERT"
    ).length;

    return {
      total,
      online,
      offline,
      disabled,
      alert,
    };
  }, [allDevices]);

  const cameraDevices = useMemo(() => {
    return allDevices.filter((item) => {
      const type = normalizeDeviceTypeValue(item?.deviceType);

      return type === "MINTCAM" || type === "MOBILE_CCTV";
    });
  }, [allDevices]);

  const watchDevices = useMemo(() => {
    return allDevices.filter(
      (item) => normalizeDeviceTypeValue(item?.deviceType) === "MINTWATCH"
    );
  }, [allDevices]);

  const cameraSummary = useMemo(() => {
    const items = cameraDevices;

    return {
      total: items.length,
      online: items.filter(
        (item) => String(item.status).toUpperCase() === "ONLINE"
      ).length,
      offline: items.filter(
        (item) => String(item.status).toUpperCase() === "OFFLINE"
      ).length,
      disabled: items.filter(
        (item) => String(item.status).toUpperCase() === "DISABLED"
      ).length,
      alert: items.filter(
        (item) => String(item.status).toUpperCase() === "ALERT"
      ).length,
    };
  }, [cameraDevices]);

  const mintwatchSummary = useMemo(() => {
    const items = watchDevices;

    return {
      total: items.length,
      online: items.filter(
        (item) => String(item.status).toUpperCase() === "ONLINE"
      ).length,
      offline: items.filter(
        (item) => String(item.status).toUpperCase() === "OFFLINE"
      ).length,
      disabled: items.filter(
        (item) => String(item.status).toUpperCase() === "DISABLED"
      ).length,
      alert: items.filter(
        (item) => String(item.status).toUpperCase() === "ALERT"
      ).length,
    };
  }, [watchDevices]);

  const uniqueCameraTypes = useMemo(() => {
    return Array.from(
      new Set(cameraDevices.map((item) => normalizeDeviceTypeValue(item.deviceType)))
    ).filter(Boolean);
  }, [cameraDevices]);

  const cameraCardTitle = useMemo(() => {
    if (uniqueCameraTypes.length === 1) {
      return `${deviceTypeText(uniqueCameraTypes[0])} 상태`;
    }

    return "카메라 상태";
  }, [uniqueCameraTypes]);

  const cameraCardBadgeText = useMemo(() => {
    if (uniqueCameraTypes.length === 1) {
      return deviceTypeText(uniqueCameraTypes[0]);
    }

    return "카메라";
  }, [uniqueCameraTypes]);

  const cameraCardBadgeClass = useMemo(() => {
    if (uniqueCameraTypes.length === 1) {
      return deviceTypeBadgeClass(uniqueCameraTypes[0]);
    }

    return "bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300";
  }, [uniqueCameraTypes]);

  const visibleTypeOptions = useMemo(() => {
    const options = [{ value: "ALL", label: "전체 종류" }];

    const presentTypes = Array.from(
      new Set(allDevices.map((item) => normalizeDeviceTypeValue(item.deviceType)))
    );

    if (presentTypes.includes("MINTCAM")) {
      options.push({ value: "MINTCAM", label: "민트캠" });
    }

    if (presentTypes.includes("MOBILE_CCTV")) {
      options.push({ value: "MOBILE_CCTV", label: "이동형 CCTV" });
    }

    if (canWatch && presentTypes.includes("MINTWATCH")) {
      options.push({ value: "MINTWATCH", label: "민트워치" });
    }

    return options;
  }, [allDevices, canWatch]);

  const filteredDevices = useMemo(() => {
    const q = String(search || "").trim().toLowerCase();

    return allDevices.filter((item) => {
      const itemType = normalizeDeviceTypeValue(item?.deviceType);
      const itemStatus = String(item?.status || "").toUpperCase();

      const typeMatch = typeFilter === "ALL" || itemType === typeFilter;
      if (!typeMatch) return false;

      const statusMatch = statusFilter === "ALL" || itemStatus === statusFilter;
      if (!statusMatch) return false;

      if (quickFilter === "MINTCAM" && itemType !== "MINTCAM") return false;
      if (quickFilter === "MOBILE_CCTV" && itemType !== "MOBILE_CCTV") return false;
      if (quickFilter === "MINTWATCH" && itemType !== "MINTWATCH") return false;

      if (
        quickFilter === "ISSUE_ONLY" &&
        itemStatus !== "OFFLINE" &&
        itemStatus !== "ALERT" &&
        itemStatus !== "DISABLED"
      ) {
        return false;
      }

      if (!q) return true;

      const haystack = [
        item?.name,
        item?.deviceId,
        item?.ownerName,
        item?.siteId,
        item?.camNoText,
        deviceTypeText(item?.deviceType),
        statusLabelText(item?.status),
        getGpsText(item),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(q);
    });
  }, [allDevices, search, typeFilter, statusFilter, quickFilter]);

  useEffect(() => {
    if (!canWatch) {
      if (typeFilter === "MINTWATCH") setTypeFilter("ALL");
      if (quickFilter === "MINTWATCH") setQuickFilter("ALL");
    }
  }, [canWatch, typeFilter, quickFilter]);

  // 장비 목록 페이지네이션 (스크롤 대신 페이지로) — 화면에 다 들어가게 6개
  const DEVICE_PAGE_SIZE = 6;
  const devicePageCount = Math.max(
    1,
    Math.ceil(filteredDevices.length / DEVICE_PAGE_SIZE)
  );
  const pagedDevices = useMemo(() => {
    const safePage = Math.min(Math.max(devicePage, 1), devicePageCount);
    const start = (safePage - 1) * DEVICE_PAGE_SIZE;
    return filteredDevices.slice(start, start + DEVICE_PAGE_SIZE);
  }, [filteredDevices, devicePage, devicePageCount]);

  // 검색/필터 바뀌면 1페이지로
  useEffect(() => {
    setDevicePage(1);
  }, [search, typeFilter, statusFilter, quickFilter]);

  useEffect(() => {
    if (devicePage > devicePageCount) setDevicePage(devicePageCount);
  }, [devicePage, devicePageCount]);

  return (
    <div className="overflow-visible pr-1 pb-[calc(96px+env(safe-area-inset-bottom))] lg:pb-2">
      <div className="space-y-3">
        <section className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-5">
          <SummaryCard
            compact
            label="전체 장비"
            value={summary.total}
            accent="text-blue-600 dark:text-blue-300"
          />

          <SummaryCard
            compact
            label="온라인"
            value={summary.online}
            accent="text-emerald-600 dark:text-emerald-300"
          />

          <SummaryCard
            compact
            label="오프라인"
            value={summary.offline}
            accent="text-gray-500 dark:text-gray-400"
          />

          <SummaryCard
            compact
            label="비활성"
            value={summary.disabled}
            accent="text-slate-600 dark:text-slate-300"
          />

          <SummaryCard
            compact
            label="위험 상태"
            value={summary.alert}
            accent="text-red-600 dark:text-red-300"
          />
        </section>

        {canWatch && (
          <section className="grid grid-cols-1 gap-3 xl:grid-cols-2">
            <DeviceTypeSummaryCard
              title={cameraCardTitle}
              badge={cameraCardBadgeText}
              badgeClass={cameraCardBadgeClass}
              total={cameraSummary.total}
              online={cameraSummary.online}
              offline={cameraSummary.offline}
              alert={cameraSummary.alert}
              disabled={cameraSummary.disabled}
            />

            <DeviceTypeSummaryCard
              title="민트워치 상태"
              badge="민트워치"
              badgeClass="bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300"
              total={mintwatchSummary.total}
              online={mintwatchSummary.online}
              offline={mintwatchSummary.offline}
              alert={mintwatchSummary.alert}
              disabled={mintwatchSummary.disabled}
            />
          </section>
        )}

        <section className="rounded-[28px] border border-white/70 bg-white/92 p-4 shadow-[0_16px_40px_rgba(15,23,42,0.06)] dark:border-white/10 dark:bg-[#111827]/82">
          <div className="mb-3 flex flex-col gap-2">
            <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
              <div>
                <div className="text-lg font-bold text-gray-900 dark:text-white">
                  현재 장비 목록
                </div>
                <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {loading
                    ? "불러오는 중..."
                    : `총 ${filteredDevices.length}대 · 업데이트 ${
                        lastUpdated ? formatKST(lastUpdated) : "-"
                      }`}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <QuickFilterButton
                  active={quickFilter === "ALL"}
                  onClick={() => setQuickFilter("ALL")}
                >
                  전체
                </QuickFilterButton>

                {uniqueCameraTypes.includes("MINTCAM") && (
                  <QuickFilterButton
                    active={quickFilter === "MINTCAM"}
                    onClick={() => setQuickFilter("MINTCAM")}
                  >
                    민트캠
                  </QuickFilterButton>
                )}

                {uniqueCameraTypes.includes("MOBILE_CCTV") && (
                  <QuickFilterButton
                    active={quickFilter === "MOBILE_CCTV"}
                    onClick={() => setQuickFilter("MOBILE_CCTV")}
                  >
                    이동형 CCTV
                  </QuickFilterButton>
                )}

                {canWatch && (
                  <QuickFilterButton
                    active={quickFilter === "MINTWATCH"}
                    onClick={() => setQuickFilter("MINTWATCH")}
                  >
                    민트워치
                  </QuickFilterButton>
                )}

                <QuickFilterButton
                  active={quickFilter === "ISSUE_ONLY"}
                  onClick={() => setQuickFilter("ISSUE_ONLY")}
                >
                  이상 장비만
                </QuickFilterButton>

                <button
                  type="button"
                  onClick={() => fetchDevices({ silent: true })}
                  className="shrink-0 rounded-xl bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                >
                  {refreshing ? "갱신 중..." : "새로고침"}
                </button>
              </div>
            </div>

            <div className="flex w-full flex-col gap-3 xl:flex-row">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="검색어 입력 (장비명, ID, 담당자, 위치)"
                className="h-11 w-full rounded-2xl border border-gray-200 bg-white px-4 text-sm outline-none focus:border-blue-400 dark:border-white/10 dark:bg-[#0F172A] dark:text-white xl:w-[320px]"
              />

              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="h-11 w-full rounded-2xl border border-gray-200 bg-white px-4 text-sm outline-none focus:border-blue-400 dark:border-white/10 dark:bg-[#0F172A] dark:text-white xl:w-[170px]"
              >
                {visibleTypeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="h-11 w-full rounded-2xl border border-gray-200 bg-white px-4 text-sm outline-none focus:border-blue-400 dark:border-white/10 dark:bg-[#0F172A] dark:text-white xl:w-[170px]"
              >
                <option value="ALL">전체 상태</option>
                <option value="ONLINE">온라인</option>
                <option value="OFFLINE">오프라인</option>
                <option value="DISABLED">비활성</option>
                <option value="ALERT">위험</option>
              </select>
            </div>
          </div>

          <DeviceTable loading={loading} devices={pagedDevices} />

          <DeviceMobileList loading={loading} devices={pagedDevices} />

          {devicePageCount > 1 && (
            <div className="mt-3 flex items-center justify-end gap-2 border-t border-gray-100 pt-3 dark:border-white/10">
              <button
                type="button"
                onClick={() => setDevicePage((p) => Math.max(1, p - 1))}
                disabled={devicePage <= 1}
                className="rounded-lg bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:cursor-default disabled:opacity-40 dark:bg-white/10 dark:text-gray-200 dark:hover:bg-white/15"
              >
                ◀ 이전
              </button>
              <div className="min-w-[56px] text-center text-sm font-semibold text-gray-600 dark:text-gray-300">
                {devicePage} / {devicePageCount}
              </div>
              <button
                type="button"
                onClick={() => setDevicePage((p) => Math.min(devicePageCount, p + 1))}
                disabled={devicePage >= devicePageCount}
                className="rounded-lg bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:cursor-default disabled:opacity-40 dark:bg-white/10 dark:text-gray-200 dark:hover:bg-white/15"
              >
                다음 ▶
              </button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}